import React, { useState, useEffect, Fragment, MutableRefObject, FC } from 'react';
import { Route, withRouter } from 'react-router-dom';
import track, { TrackingProp } from 'react-tracking';
import Address from '@ynap/address';
import BEMHelper from '@ynap/bem-helper';
import { SimpleAsyncButton } from '@ynap/button';
import Notification, { Tooltip } from '@ynap/notification';
import { Form, Field as FormField, FormValues, FieldRenderProps, FormRefObject } from '@ynap/form';
import { StopPropagation } from '@ynap/overlay';
import { RadioButtonGroup, RadioButton } from '@ynap/radio-button';
import { AddressForm } from '../AddressForm';
import { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';
import { mapAddressValues, collateErrors } from '../AddressForm/utils';
import AddAddressRadioButton from './AddAddressRadioButton';
import AddAddressRoute from './AddAddressRoute';
import AddressRadioButton from './AddressRadioButton';
import EditAddressRoute from './EditAddressRoute';
import { AddAddressAction, EditAddressAction } from './@types';

type AnyFn = (...args: unknown[]) =>  Partial<{}>;
const bem = new BEMHelper('AddressSelectionForm');

interface AddressSelectionFormProps {
    addAddress?: AddAddressAction;
    addresses?: Array<FormValues>;
    addressFormTouched?: boolean;
    addressIdValue: number;
    addTitle: string;
    childRef?: MutableRefObject<FormRefObject>;
    className: string;
    config: Config;
    defaultAddress?: FormValues;
    defaultAddressLabel?: string;
    editAddress?: EditAddressAction;
    editTitle: string;
    error?: string;
    form: string;
    history: { push: (url: string) => void };
    initialValues?: FormValues;
    locale: Locale;
    name?: string;
    onSubmit?: AnyFn;
    otherAddressLabel?: string;
    shippingRestricted?: boolean;
    submitting?: boolean;
    match: { path: string; url: string };
    messages?: Messages;
    reduxForm?: { change: AnyFn; name: string; blur: any; syncErrors: any };
    trackAddNewAddress?: AnyFn;
    trackConfirmAddNewAddress?: () => void;
    trackEditAddress?: AnyFn;
    trackUpdateAddress?: AnyFn;
    trackOnSubmitButtonClick?: AnyFn;
    trackAddressShippingRestricted?: AnyFn;
    tracking: TrackingProp;
    isCheckoutLayout?: boolean;
    isSelectedAddressValid?: boolean;
    editable?: boolean;
    showChangeAddress?: boolean;
    onChange?: AnyFn;
    split?: boolean;
    hideCountry?: boolean;
    addressFormClass?: string;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    isGuestUser?: boolean;
    setAddressForm?: AnyFn;
    setSelectAddressForm?: AnyFn;
    shouldDisplayAddressForm?: boolean;
    onEditModeChange?: AnyFn;
    handleFormCancel?: AnyFn;
    billingAddressValidation?: { address: number; invalidFields: string[] };
}

type EditAddressState = number | string;

export const AddressSelectionForm: FC<AddressSelectionFormProps> = (props) => {
    const {
        addAddress,
        addresses = [],
        addressFormTouched,
        childRef,
        className,
        config,
        defaultAddress,
        defaultAddressLabel,
        editAddress,
        error,
        hideCountry = false,
        history,
        initialValues,
        locale,
        match,
        messages = {},
        name,
        otherAddressLabel,
        addTitle,
        editTitle,
        onSubmit,
        reduxForm,
        shippingRestricted = false,
        trackUpdateAddress,
        tracking,
        trackAddressShippingRestricted,
        trackOnSubmitButtonClick,
        trackConfirmAddNewAddress,
        trackAddNewAddress,
        trackEditAddress,
        isCheckoutLayout = true,
        isSelectedAddressValid,
        editable = true,
        showChangeAddress = false,
        onChange,
        setAddressForm,
        setSelectAddressForm,
        shouldDisplayAddressForm,
        split = false,
        addressFormClass,
        primaryCategory,
        checkoutAnalytics,
        isGuestUser,
        handleFormCancel,
        billingAddressValidation,
    } = props;

    const addressIdValue =
        initialValues && initialValues.addressId && (typeof initialValues.addressId == 'string' || typeof initialValues.addressId == 'number')
            ? initialValues.addressId.toString()
            : undefined;
    const isRegisteredAndRestricted = shippingRestricted && addresses.length && initialValues?.addressId === Number(addressIdValue);

    const [enableContinue, setEnableContinue] = useState(!isRegisteredAndRestricted);
    const [removeAddressFinder, setRemoveAddressFinder] = useState(() => () => null);

    // We had an issue (SHOP-4546) while editing an already existing address mid checkout flow where we would have unnecessary
    // renders of the address list after the address was edited and before the cart api was called with the updated address id.
    // So we had to come up with a mechanism to track this flow and prevent those renders from happening since they
    // didn't add anything to the user since there was no interaction available anyway.
    // This will also help along the line using !! to force cast to boolean
    // 0 -> start
    // 1 -> during
    // > 1 -> the id of the address that was just edited (also used to pass the address id to relevante components)
    const [saveEditing, setSaveEditing] = useState(0);

    const invalidSelectedAddress = isSelectedAddressValid === false ? addressIdValue : '';
    const [editAddressID, setEditAddressID] = useState<EditAddressState | undefined>(invalidSelectedAddress);

    // useEffect to open the address form when any field in the selected billing address is invalid.
    useEffect(() => {
        if (Object.prototype.hasOwnProperty.call(billingAddressValidation ?? {}, 'invalidFields') && billingAddressValidation?.invalidFields?.length) {
            setEditAddressID(Number(billingAddressValidation.address));
        }
    }, [billingAddressValidation]);

    // We setEditAddressID if we know the user clicked a billing address with Klarna payment
    useEffect(() => {
        if (reduxForm?.syncErrors?.billingAddress?.isKlarna) {
            setEditAddressID(Number(reduxForm?.syncErrors?.billingAddress?.addressId));
        }
    }, [reduxForm?.syncErrors]);

    useEffect(() => {
        const isDefaultAddress = addresses.some((address) => initialValues && initialValues.addressId === address.addressId);
        shippingRestricted && trackAddressShippingRestricted && tracking.trackEvent(trackAddressShippingRestricted && trackAddressShippingRestricted({ defaultAddress: isDefaultAddress }));
    }, [shippingRestricted]);

    useEffect(() => {
        // show the address form if user choose an invalid address
        if (invalidSelectedAddress) setEditAddressID(Number(invalidSelectedAddress));
    }, [invalidSelectedAddress]);

    const handleSubmit = async (values: FormValues) => {
        const mappedValues = mapAddressValues(values, locale);
        trackConfirmAddNewAddress?.();
        onChange?.(mappedValues);
        return onSubmit?.(mappedValues);
    };
    const formClass = className ? bem(className, 'isCheckoutLayout') : bem(undefined, 'isCheckoutLayout');

    const isEditing = (Array.isArray(addresses) && !!addresses.find((it) => it.addressId === editAddressID)) || !!saveEditing;
    const isAdding = editAddressID === 'add' || (!isEditing && shouldDisplayAddressForm);

    useEffect(() => {
        if (isAdding || isEditing) {
            setAddressForm?.(true);
        }
    }, [isEditing, isAdding]);

    return (
        <Form
            name={name ? name : 'address-form'}
            className={formClass}
            initialValues={initialValues}
            onSubmit={onSubmit ? handleSubmit : undefined}
            touched={addressFormTouched}
            reduxForm={reduxForm}
            ref={childRef}
            messages={messages}
            collateErrors={collateErrors}
        >
            {(submitting: boolean, errors: Array<string>) => (
                <Fragment>
                    {!editable && addAddress && !addresses?.length ? (
                        <Fragment>
                            <StopPropagation>
                                <AddAddressRoute
                                    addAddress={addAddress}
                                    addTitle={addTitle}
                                    config={config}
                                    history={history}
                                    locale={locale}
                                    match={match}
                                    messages={messages}
                                    trackConfirmAddNewAddress={trackConfirmAddNewAddress}
                                    withOverlay={false}
                                    isCheckoutLayout={isCheckoutLayout}
                                    formTitle={messages?.addressSelectionForm?.addAddress?.()}
                                    setEditAddressID={setEditAddressID}
                                    onSubmit={handleSubmit}
                                    hideCountry={hideCountry}
                                    tracking={tracking}
                                    primaryCategory={primaryCategory}
                                    checkoutAnalytics={checkoutAnalytics}
                                    removeAddressFinder={removeAddressFinder}
                                    setRemoveAddressFinder={setRemoveAddressFinder}
                                />
                            </StopPropagation>
                        </Fragment>
                    ) : !addresses || !addresses.length ? (
                        defaultAddress ? (
                            <Fragment>
                                <FormField
                                    name="addressId"
                                    value={String(initialValues?.addressId || defaultAddress?.addressId || addresses[0]?.addressId || '')}
                                >
                                    {({ value, onChange }: FieldRenderProps) => {
                                        return (
                                            <Fragment>
                                                <RadioButton
                                                    id={String(defaultAddress.addressId)}
                                                    className={bem('radioButton')}
                                                    checked={typeof value == 'undefined' || value == defaultAddress.addressId}
                                                    value={defaultAddress.addressId}
                                                    label={defaultAddressLabel}
                                                    description={<Address {...defaultAddress} />}
                                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onChange(ev.target.value)}
                                                />
                                                <RadioButton
                                                    id="newBillingAddress"
                                                    className={bem('radioButton')}
                                                    checked={value != defaultAddress.addressId}
                                                    value={addressIdValue !== defaultAddress.addressId ? addressIdValue : ''}
                                                    label={otherAddressLabel}
                                                    onChange={() => {
                                                        const newAddressId = addressIdValue != defaultAddress.addressId ? addressIdValue || '' : '';
                                                        onChange(newAddressId);
                                                    }}
                                                />
                                                {value != defaultAddress.addressId ? (
                                                    <AddressForm
                                                        className={addressFormClass || bem('addressForm')}
                                                        config={config}
                                                        country={locale.country}
                                                        initialValues={initialValues}
                                                        messages={messages}
                                                        isCheckoutLayout={isCheckoutLayout}
                                                        hideCountry={hideCountry}
                                                        tracking={tracking}
                                                        primaryCategory={primaryCategory}
                                                        checkoutAnalytics={checkoutAnalytics}
                                                        removeAddressFinder={removeAddressFinder}
                                                        setRemoveAddressFinder={setRemoveAddressFinder}
                                                    />
                                                ) : null}
                                            </Fragment>
                                        );
                                    }}
                                </FormField>
                            </Fragment>
                        ) : (
                            <>
                                <AddressForm
                                    config={config}
                                    country={locale.country}
                                    initialValues={initialValues}
                                    messages={messages}
                                    className={addressFormClass || bem('addressForm')}
                                    isCheckoutLayout={isCheckoutLayout}
                                    hideCountry={hideCountry}
                                    tracking={tracking}
                                    primaryCategory={primaryCategory}
                                    checkoutAnalytics={checkoutAnalytics}
                                    removeAddressFinder={removeAddressFinder}
                                    setRemoveAddressFinder={setRemoveAddressFinder}
                                />
                                {shippingRestricted && (
                                    <Notification
                                        className={bem('shipping-restricted')}
                                        type={'error'}
                                        message={messages?.notifications?.shippingRestricted?.() || ''}
                                    />
                                )}
                            </>
                        )
                    ) : (
                        <Fragment>
                            {addresses.length > 0 ? (
                                <RadioButtonGroup divided className={bem('radioGroup', { split })} removeLastDivider={isEditing}>
                                    {addresses.map((address: FormValues) => {
                                        const isRestricted = initialValues && initialValues.addressId === address.addressId && shippingRestricted;

                                        // if its checkoutV3 and we have just edited an address, we change the address Id to the edited one's id
                                        // this will ensure we don't render anything once the edit address is actually done and before we call
                                        // cart api with new address id
                                        // otherwise everything goes as normal
                                        const actualEditAddressId = saveEditing > 1 ? saveEditing : editAddressID;
                                        return !isEditing || actualEditAddressId === address.addressId ? (
                                            <AddressRadioButton
                                                // This ensures we only pass 'setSaveEditing' if it's checkoutV3, so we don't break things before v3 is deployed
                                                setSaveEditing={setSaveEditing}
                                                saveEditing={saveEditing}
                                                key={Number(address.addressId)}
                                                address={address}
                                                isRestricted={isRestricted}
                                                editAddress={editAddress}
                                                messages={messages}
                                                match={match}
                                                trackEditAddress={() => trackEditAddress?.()}
                                                defaultAddress={defaultAddress ? String(defaultAddress.addressId) : undefined}
                                                onChange={() => {
                                                    setEnableContinue(true);
                                                    onChange?.(address.addressId);
                                                    setSelectAddressForm?.(true);
                                                    setAddressForm?.(false);
                                                }}
                                                isCheckoutLayout={isCheckoutLayout}
                                                config={config}
                                                locale={locale}
                                                trackUpdateAddress={trackUpdateAddress}
                                                setEditAddressID={setEditAddressID}
                                                editAddressID={actualEditAddressId}
                                                onSubmit={handleSubmit}
                                                onCancel={() => {
                                                    const klarnaPhoneNoError = reduxForm?.syncErrors?.billingAddress?.isKlarna;

                                                    setEditAddressID('');
                                                    setSelectAddressForm?.(klarnaPhoneNoError);
                                                    setAddressForm?.(false);
                                                    handleFormCancel && handleFormCancel();
                                                }}
                                                editable={editable}
                                                hideCountry={hideCountry}
                                                tracking={tracking}
                                                primaryCategory={primaryCategory}
                                                checkoutAnalytics={checkoutAnalytics}
                                                split={split}
                                                isGuestUser={isGuestUser}
                                                reduxForm={reduxForm}
                                                removeAddressFinder={removeAddressFinder}
                                                setRemoveAddressFinder={setRemoveAddressFinder}
                                                invalidFields={billingAddressValidation?.invalidFields || []}
                                            />
                                        ) : null;
                                    })}
                                    {showChangeAddress && (
                                        <FormField name="addressId" key="changeAddress" value="">
                                            {({ value, ...rest }: FieldRenderProps) => {
                                                const checked = value === 'changeAddress';
                                                return (
                                                    <RadioButton
                                                        {...rest}
                                                        id="changeAddress"
                                                        key="changeAddress"
                                                        value="changeAddress"
                                                        checked={checked}
                                                        className={bem('changeAddress', split ? 'split' : undefined, 'radio')}
                                                        label={messages.shippingAddressForm?.changeAddress?.()}
                                                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                                            onChange?.(ev.target.value);
                                                        }}
                                                    />
                                                );
                                            }}
                                        </FormField>
                                    )}
                                    {addAddress && !isEditing && !showChangeAddress && (
                                        <AddAddressRadioButton
                                            address="addAddress"
                                            messages={messages}
                                            onChange={() => {
                                                setEditAddressID('add');
                                                onChange?.('add');
                                            }}
                                            split={split}
                                            trackAddNewAddress={trackAddNewAddress}
                                        />
                                    )}
                                </RadioButtonGroup>
                            ) : null}

                            {addAddress && isAdding ? (
                                <StopPropagation>
                                    <AddAddressRoute
                                        addAddress={addAddress}
                                        addTitle={addTitle}
                                        config={config}
                                        history={history}
                                        locale={locale}
                                        match={match}
                                        messages={messages}
                                        trackConfirmAddNewAddress={trackConfirmAddNewAddress}
                                        withOverlay={false}
                                        isCheckoutLayout={isCheckoutLayout}
                                        formTitle={messages.addressSelectionForm?.addAddress?.()}
                                        setEditAddressID={setEditAddressID}
                                        onSubmit={handleSubmit}
                                        hideCountry={hideCountry}
                                        tracking={tracking}
                                        primaryCategory={primaryCategory}
                                        checkoutAnalytics={checkoutAnalytics}
                                        removeAddressFinder={removeAddressFinder}
                                        setRemoveAddressFinder={setRemoveAddressFinder}
                                    />
                                </StopPropagation>
                            ) : null}
                            {!addAddress && (
                                <Route
                                    path={`${match.path}/edit/:addressId`}
                                    exact
                                    render={({ match: editMatch }: any) => {
                                        return (
                                            <EditAddressRoute
                                                addresses={addresses}
                                                config={config}
                                                editAddress={editAddress}
                                                editTitle={editTitle}
                                                history={history}
                                                locale={locale}
                                                editMatch={editMatch}
                                                match={match}
                                                messages={messages}
                                                trackUpdateAddress={trackUpdateAddress}
                                                isCheckoutLayout={isCheckoutLayout}
                                                withOverlay={true}
                                                hideCountry={hideCountry}
                                                tracking={tracking}
                                                primaryCategory={primaryCategory}
                                                checkoutAnalytics={checkoutAnalytics}
                                                removeAddressFinder={removeAddressFinder}
                                                setRemoveAddressFinder={setRemoveAddressFinder}
                                            />
                                        );
                                    }}
                                />
                            )}
                            {!addAddress && (
                                <Route
                                    path={`${match.path}/add`}
                                    exact
                                    render={() => (
                                        <AddAddressRoute
                                            addAddress={addAddress}
                                            addTitle={addTitle}
                                            config={config}
                                            history={history}
                                            locale={locale}
                                            match={match}
                                            messages={messages}
                                            trackConfirmAddNewAddress={trackConfirmAddNewAddress}
                                            isCheckoutLayout={isCheckoutLayout}
                                            hideCountry={hideCountry}
                                            tracking={tracking}
                                            primaryCategory={primaryCategory}
                                            checkoutAnalytics={checkoutAnalytics}
                                            removeAddressFinder={removeAddressFinder}
                                            setRemoveAddressFinder={setRemoveAddressFinder}
                                        />
                                    )}
                                />
                            )}
                        </Fragment>
                    )}

                    {!reduxForm && !(isAdding || isEditing) ? (
                        <Tooltip
                            className={bem('buttonWrapper', 'isCheckoutLayout')}
                            childWrapperClassName={'checkout'}
                            type="error"
                            message={errors?.[0]}
                            defaultOpen={!submitting && !!errors.length}
                            open={!submitting && !!errors.length}
                            key={error}
                        >
                            <SimpleAsyncButton
                                copy={messages.addressSelectionForm?.submit?.()}
                                type="primary"
                                className={bem('submit')}
                                loading={submitting}
                                disabled={!!isRegisteredAndRestricted && !enableContinue}
                                onClick={() => {
                                    removeAddressFinder?.();
                                    trackOnSubmitButtonClick?.();
                                }}
                            />
                        </Tooltip>
                    ) : null}
                </Fragment>
            )}
        </Form>
    );
};

export default withRouter(track()(AddressSelectionForm));
